/* ==========================================================================
   Markdown tối giản cho câu trả lời của trợ lý.

   Nguyên tắc an toàn, theo đúng thứ tự này và không được đảo:
     1. Bỏ ký tự sentinel khỏi đầu vào, để câu trả lời không giả mạo được
        placeholder mà chèn HTML thô.
     2. Rút khối code ra, thay bằng placeholder.
     3. Escape toàn bộ HTML.
     4. Mới bắt đầu sinh thẻ.

   Vì escape xảy ra TRƯỚC khi sinh thẻ, mọi `<script>` trong câu trả lời đều
   hiện ra dạng chữ. Không dùng thư viện markdown ngoài: chúng kéo theo vài
   chục KB và mỗi cái lại có lịch sử XSS riêng.
   ========================================================================== */

import { parseChart, renderChart, CHART_PENDING } from "../ui/chart.ts";
import { renderPreview, renderPrint, HTML_PENDING } from "../ui/html-preview.ts";

const SENTINEL = "";
const BLOCK_RE = new RegExp(SENTINEL + "B(\\d+)" + SENTINEL, "g");
const INLINE_RE = new RegExp(SENTINEL + "I(\\d+)" + SENTINEL, "g");
const STASH_RE = new RegExp(SENTINEL + "P(\\d+)" + SENTINEL, "g");
const LONE_BLOCK_RE = new RegExp("^" + SENTINEL + "B\\d+" + SENTINEL + "$");

/** Chỉ ba giao thức này được phép thành href. `javascript:` và `data:` bị
 *  loại — đó là đường XSS kinh điển qua link trong câu trả lời của LLM. */
const SAFE_PROTOCOL = /^(https?:\/\/|mailto:|tel:)/i;

export function escapeHtml(input: string): string {
  return String(input)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const PREVIEW_LANGS = new Set(["html", "htm", "svg"]);

/** `live=false` khi câu trả lời còn đang stream: khối html/svg chưa dựng iframe (tránh nạp lại mỗi mẩu chữ).
 *  `print=true` (PDF trên server, JavaScript tắt): khối html/svg đặt thẳng trong shadow root khai báo. */
export function renderMarkdown(input: string, opts: { live?: boolean; print?: boolean } = {}): string {
  const live = opts.live !== false;
  let source = String(input ?? "").split(SENTINEL).join("");

  const codeBlocks: string[] = [];
  /* Khối ```chart / ```html chưa đóng (đang stream): giữ chỗ thay vì hiện JSON hay mã dở. */
  let pending = "";
  const opener = /```(chart|html|htm|svg)\b/g;
  let last: RegExpExecArray | null = null;
  for (let m = opener.exec(source); m; m = opener.exec(source)) last = m;
  if (last && (source.slice(0, last.index).match(/```/g) || []).length % 2 === 0 && source.indexOf("```", last.index + 3) < 0) {
    pending = last[1] === "chart" ? CHART_PENDING : HTML_PENDING;
    source = source.slice(0, last.index);
  }
  let text = source.replace(/```([a-zA-Z0-9_+-]*)\n?([\s\S]*?)```/g, (_all, lang: string, body: string) => {
    if (lang === "chart") {
      /* JSON → SVG do chính widget vẽ; JSON hỏng thì không hiện gì thô */
      const spec = parseChart(body);
      codeBlocks.push(spec ? renderChart(spec) : CHART_PENDING);
      return "\n" + SENTINEL + "B" + (codeBlocks.length - 1) + SENTINEL + "\n";
    }
    if (PREVIEW_LANGS.has(lang.toLowerCase())) {
      codeBlocks.push(opts.print ? renderPrint(body) : renderPreview(body, lang.toLowerCase(), live));
      return "\n" + SENTINEL + "B" + (codeBlocks.length - 1) + SENTINEL + "\n";
    }
    const safeLang = String(lang || "").replace(/[^a-z0-9_+-]/gi, "");
    const cls = safeLang ? ' class="apx-lang-' + safeLang + '"' : "";
    codeBlocks.push(
      '<pre class="apx-pre"><code' + cls + ">" + escapeHtml(body.replace(/\n$/, "")) + "</code></pre>",
    );
    return SENTINEL + "B" + (codeBlocks.length - 1) + SENTINEL;
  });

  const inlineCode: string[] = [];
  text = text.replace(/`([^`\n]+)`/g, (_all, body: string) => {
    inlineCode.push('<code class="apx-code">' + escapeHtml(body) + "</code>");
    return SENTINEL + "I" + (inlineCode.length - 1) + SENTINEL;
  });

  if (pending) {
    codeBlocks.push(pending);
    text += "\n" + SENTINEL + "B" + (codeBlocks.length - 1) + SENTINEL;
  }

  text = escapeHtml(text);

  return blocks(text)
    .replace(BLOCK_RE, (_all, i: string) => codeBlocks[Number(i)] ?? "")
    .replace(INLINE_RE, (_all, i: string) => inlineCode[Number(i)] ?? "");
}

function blocks(text: string): string {
  const lines = text.split(/\r?\n/);
  const out: string[] = [];
  const bullet = /^\s*([-*+])\s+/;
  const ordered = /^\s*\d+[.)]\s+/;
  let index = 0;

  while (index < lines.length) {
    const line = lines[index] as string;

    if (!line.trim()) {
      index++;
      continue;
    }

    /* Placeholder khối code đứng một mình — nhả thẳng, không bọc <p>. */
    if (LONE_BLOCK_RE.test(line.trim())) {
      out.push(line.trim());
      index++;
      continue;
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      /* # → h3: heading trong bong bóng chat không được tranh cấp bậc với
         heading thật của trang khách. */
      const level = (heading[1] as string).length + 2;
      out.push("<h" + level + ' class="apx-h">' + inline(heading[2] as string) + "</h" + level + ">");
      index++;
      continue;
    }

    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      out.push('<hr class="apx-hr">');
      index++;
      continue;
    }

    /* Dấu > đã bị escape thành &gt; ở bước trên. */
    if (/^\s*&gt;\s?/.test(line)) {
      const quoted: string[] = [];
      while (index < lines.length && /^\s*&gt;\s?/.test(lines[index] as string)) {
        quoted.push((lines[index] as string).replace(/^\s*&gt;\s?/, ""));
        index++;
      }
      out.push('<blockquote class="apx-quote">' + inline(quoted.join(" ")) + "</blockquote>");
      continue;
    }

    /* Bảng kiểu GFM: dòng tiêu đề có `|`, ngay sau là dòng `|---|---:|`.
       Chạy sau bước escape nên ô bảng không thể chứa thẻ thô. */
    if (isTableStart(lines, index)) {
      const head = cells(line);
      const align = cells(lines[index + 1] as string).map((c) =>
        /:-+:$/.test(c) ? "center" : /-+:$/.test(c) ? "right" : "",
      );
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && /\|/.test(lines[index] as string) && (lines[index] as string).trim()) {
        rows.push(cells(lines[index] as string));
        index++;
      }
      const td = (tag: string, c: string, i: number) =>
        "<" + tag + (align[i] ? ' class="apx-' + align[i] + '"' : "") + ">" + inline(c) + "</" + tag + ">";
      out.push(
        '<div class="apx-tw"><table class="apx-table"><thead><tr>' +
          head.map((c, i) => td("th", c, i)).join("") +
          "</tr></thead><tbody>" +
          rows.map((r) => "<tr>" + head.map((_h, i) => td("td", r[i] ?? "", i)).join("") + "</tr>").join("") +
          "</tbody></table></div>",
      );
      continue;
    }

    if (bullet.test(line) || ordered.test(line)) {
      const isOrdered = ordered.test(line);
      const matcher = isOrdered ? ordered : bullet;
      const items: string[] = [];
      while (index < lines.length && matcher.test(lines[index] as string)) {
        let item = (lines[index] as string).replace(matcher, "");
        index++;
        /* Dòng thụt lề ngay sau thuộc về cùng một mục. */
        while (
          index < lines.length &&
          /^\s{2,}\S/.test(lines[index] as string) &&
          !matcher.test(lines[index] as string)
        ) {
          item += " " + (lines[index] as string).trim();
          index++;
        }
        items.push("<li>" + inline(item) + "</li>");
      }
      const tag = isOrdered ? "ol" : "ul";
      out.push("<" + tag + ' class="apx-list">' + items.join("") + "</" + tag + ">");
      continue;
    }

    const paragraph: string[] = [];
    while (index < lines.length) {
      const current = lines[index] as string;
      if (!current.trim()) break;
      if (bullet.test(current) || ordered.test(current)) break;
      if (/^(#{1,3})\s+/.test(current)) break;
      if (LONE_BLOCK_RE.test(current.trim())) break;
      if (isTableStart(lines, index)) break;
      paragraph.push(current);
      index++;
    }
    out.push('<p class="apx-p">' + inline(paragraph.join("\n")).replace(/\n/g, "<br>") + "</p>");
  }

  return out.join("");
}

const TABLE_SEP = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;

function isTableStart(lines: string[], index: number): boolean {
  const line = lines[index];
  const next = lines[index + 1];
  return !!line && !!next && line.indexOf("|") >= 0 && TABLE_SEP.test(next);
}

function cells(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
}

function anchor(href: string, label: string): string {
  return (
    '<a class="apx-a" href="' +
    escapeHtml(href) +
    '" target="_blank" rel="noopener noreferrer nofollow">' +
    label +
    "</a>"
  );
}

function inline(text: string): string {
  let out = text;
  /* thẻ ảnh/link đã dựng được cất ra placeholder, để bước **, _ phía dưới không chạm vào URL trong thuộc tính */
  const stash: string[] = [];
  const keep = (html: string) => SENTINEL + "P" + (stash.push(html) - 1) + SENTINEL;

  /* ![mô tả](https://…) — ảnh chỉ từ https: hoặc data:image (thẻ <img> không chạy script); bấm mở ảnh gốc. */
  out = out.replace(/!\[([^\]\n]*)\]\(([^)\s]+)\)/g, (all, alt: string, src: string) => {
    const url = src.replace(/&amp;/g, "&");
    if (!/^(https:\/\/|data:image\/(png|jpe?g|gif|webp|svg\+xml);)/i.test(url)) return all;
    const img =
      '<img class="apx-img" src="' + escapeHtml(url) + '" alt="' + alt.replace(/https?:\/\//gi, "") + '" loading="lazy" referrerpolicy="no-referrer">';
    return keep(url.startsWith("https://") ? anchor(url, img) : img);
  });

  /* [nhãn](url) — url đã qua escape nên &amp; phải trả lại trước khi kiểm tra. */
  out = out.replace(
    /\[([^\]\n]+)\]\(([^)\s]+)(?:\s+&quot;[^)]*&quot;)?\)/g,
    (all, label: string, href: string) => {
      const url = href.replace(/&amp;/g, "&");
      if (!SAFE_PROTOCOL.test(url)) return all;
      return keep(anchor(url, label));
    },
  );

  /* URL trần. Chạy sau bước trên nên phải chừa lại phần đã nằm trong href="". */
  out = out.replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g, (all, lead: string, url: string) => {
    if (/(href|src)=&quot;?$/.test(lead)) return all;
    if (url.indexOf("&quot;") >= 0) return all;
    const clean = url.replace(/[.,;:!?]+$/, "");
    const tail = url.slice(clean.length);
    return lead + keep(anchor(clean.replace(/&amp;/g, "&"), clean)) + tail;
  });

  /* đậm cho phép một dấu * lẻ bên trong ("**L* ≥ 98**") */
  out = out.replace(/\*\*((?:[^*\n]|\*(?!\*))+?)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/__([^_\n]+)__/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*\w])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
  out = out.replace(/(^|[^_\w])_([^_\n]+)_(?!_)/g, "$1<em>$2</em>");
  out = out.replace(/~~([^~\n]+)~~/g, "<del>$1</del>");

  return out.replace(STASH_RE, (_all, n: string) => stash[Number(n)] ?? "");
}
